
import React from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Edit, Eye, MoreHorizontal, Power } from "lucide-react";
import { MentoriaOffering } from "@/hooks/useMentoriasAdmin";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface MentoriaTableRowProps {
  mentoria: MentoriaOffering;
  onEdit: (mentoria: MentoriaOffering) => void;
  onViewDetails: (mentoria: MentoriaOffering) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
}

export const MentoriaTableRow: React.FC<MentoriaTableRowProps> = ({ 
  mentoria, 
  onEdit, 
  onViewDetails,
  onToggleStatus
}) => {
  const formattedDate = mentoria.created_at 
    ? format(new Date(mentoria.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
    : "Desconhecido";

  const formatPrice = (priceCents: number) => {
    if (priceCents === 0) {
      return 'Gratuito';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(priceCents / 100);
  };

  return (
    <TableRow className="border-b border-[#222A35]">
      <TableCell className="py-3">
        <div className="font-medium text-white">{mentoria.title}</div>
        <div className="text-sm text-gray-400 mt-1 line-clamp-1">
          {mentoria.description}
        </div>
      </TableCell>
      <TableCell className="text-white">
        {mentoria.mentor?.profile?.nickname || "Não atribuído"}
      </TableCell>
      <TableCell className="text-white">{mentoria.game?.name || "Não atribuído"}</TableCell>
      <TableCell className="text-white">{formatPrice(mentoria.price_cents)}</TableCell>
      <TableCell>
        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
          mentoria.is_active 
            ? "bg-green-500/20 text-green-400 border-green-500/30"
            : "bg-red-500/20 text-red-400 border-red-500/30"
        }`}>
          {mentoria.is_active ? "Ativo" : "Inativo"}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
              <span className="sr-only">Abrir menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#181D24] border-[#222A35] text-white">
            <DropdownMenuLabel>Ações</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#222A35]" />
            <DropdownMenuItem 
              className="hover:bg-[#222A35] cursor-pointer"
              onClick={() => onViewDetails(mentoria)}
            >
              <Eye className="mr-2 h-4 w-4" />
              <span>Ver detalhes</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="hover:bg-[#222A35] cursor-pointer"
              onClick={() => onEdit(mentoria)}
            >
              <Edit className="mr-2 h-4 w-4" />
              <span>Editar mentoria</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="hover:bg-[#222A35] cursor-pointer"
              onClick={() => onToggleStatus(mentoria.id, mentoria.is_active)}
            >
              <Power className="mr-2 h-4 w-4" />
              <span>{mentoria.is_active ? "Desativar" : "Ativar"} mentoria</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};
